import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from "@boozallen-uip/uswds-react-core";
import uniq from 'lodash/uniq';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faXmark, faPlus, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import './ProductTableContainer.component.css';
import ProductListTable from './ProductListTable.auth.component';
import ExternalLinkIcon from "../../../components/ExternalLinkIcon.component";
import ProductUnauthListTable from './ProductListTable.unauth.component';
import AcrExportWizard from './AcrExportWizard.component';
import TableNavigation from '../../../components/TableNavigation.component';
import SearchableSelect from '../../../components/SearchableSelect.component';
import Tooltip from '../../../components/Tooltip.component';
import useProduct from '../../../hooks/useProduct.hook';
import useProductAcrExport from '../../../hooks/useProductAcrExport.hook';
import { PAGE_SIZE, PRODUCT_SUBMIT_URL } from '../../../utlities/core.constants';
import { ProductContext } from '../ProductContext';
import { VendorContext } from '../../Vendor/VendorContext';
import { UserContext } from '../../UserContext';

// Product Category options whose label should always sink to the bottom of the filter list.
const BOTTOM_CATEGORY_LABELS = ["other", "none"];
const AVAILABILITY_RETRY_MS = 1500;
const AVAILABILITY_MAX_RETRIES = 2;

export default function ProductTableContainer({entityId = null, showVendorAction = true, myListings = false, onRequestAcr = undefined}){
  const [productsLoading, setProductsLoading] = useState(false);
  const [filterTitles, setFilterTitles] = useState([]);
  const [filterPscCodes, setFilterPscCodes] = useState([]);
  const [filterVendors, setFilterVendors] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  const [totalCount, setTotalCount] = useState(null);
  const [selectedNameFilter, setNameFilter] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedPscCodes, setSelectedPscCodes] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(PAGE_SIZE);
  const [productsList, setProductsList] = useState([]);

  // In-site (ACR Editor) ACR export state: which visible rows have a downloadable ACR, which rows
  // the user checked, and whether the file-type wizard is open.
  const [acrAvailability, setAcrAvailability] = useState({});
  const [acrAddedOn, setAcrAddedOn] = useState({});
  const [selectedExportIds, setSelectedExportIds] = useState([]);
  const [isExportWizardOpen, setExportWizardOpen] = useState(false);
  const [availabilityRetryToken, setAvailabilityRetryToken] = useState(0);
  const availabilityRequested = useRef(new Set());
  const availabilityAttempts = useRef(new Map());
  const availabilityRetryTimer = useRef(null);

  const { user, isVendorUser, isAgencyUser } = useContext(UserContext);
  const navigate = useNavigate();
  const { fetchFilteredProductAll } = useProduct();
  const { getAvailabilityMap, buildExportModel } = useProductAcrExport();
  const normalizeProducts = useCallback((response) => (Array.isArray(response) ? response : []), []);
  const getMostRecentTimestamp = useCallback((product) => {
    const mostRecentDate =
      product?.lastModifiedDate
      || product?.LastModifiedDate
      || product?.createdDate
      || product?.CreatedDate;
    const parsedDate = Date.parse(mostRecentDate);
    return Number.isNaN(parsedDate) ? 0 : parsedDate;
  }, []);
  const sortByMostRecent = useCallback((products) => (
    [...products].sort((a, b) => getMostRecentTimestamp(b) - getMostRecentTimestamp(a))
  ), [getMostRecentTimestamp]);

  const getTotalPages = useCallback(() => {
    if (!totalCount) {
      return 0;
    }
    return Math.ceil(totalCount / currentPageSize);
  }, [totalCount, currentPageSize]);

  const { categories } = useContext(ProductContext);
  const { vendors } = useContext(VendorContext);

  const renderDropdownOptions = (currentValues, valueName) => {
    if (!currentValues) {
      return [];
    }

    const normalizedOptions = currentValues
      .map((value) => value?.[valueName])
      .filter((value) => value !== undefined && value !== null)
      .map((value) => value.toString().trim())
      .filter(Boolean);

    return uniq(normalizedOptions).sort((a, b) => a.localeCompare(b));
  };

  // TODO: Remove this after we update the API to return pagination data
  const setFilters = useCallback((products) => {
    setFilterPscCodes(renderDropdownOptions(products, 'pscCode'));
  }, []);

  const normalizeFilterValue = useCallback((value) => (value || "").toString().trim().toLowerCase(), []);

  const matchProductName = useCallback((product, searchValue) => {
    const normalizedQuery = normalizeFilterValue(searchValue);
    if (!normalizedQuery) {
      return true;
    }

    const productName = normalizeFilterValue(product?.name);
    const searchTerms = normalizedQuery.split(/\s+/).filter(Boolean);

    return searchTerms.every((term) => productName.includes(term));
  }, [normalizeFilterValue]);

  // Multi-select facet match: a product passes when nothing is selected, or its value equals one of
  // the selected options (options are the exact category/vendor/PSC values, so exact membership).
  const matchesMultiFilter = useCallback((productValue, selectedValues) => {
    if (!selectedValues || selectedValues.length === 0) {
      return true;
    }
    const normalizedProduct = normalizeFilterValue(productValue);
    return selectedValues.some((value) => normalizeFilterValue(value) === normalizedProduct);
  }, [normalizeFilterValue]);

  const getProductVendorName = useCallback((product) => {
    const directVendorName = product?.vendor?.name;
    if (directVendorName) {
      return directVendorName;
    }

    const vendorId = product?.vendor?.id || product?.vendorId;
    if (!vendorId) {
      // Agency-owned (Buyer) products have no vendor; use the owning agency name so the column
      // value and any name-based matching reflect the product's actual org.
      return product?.agencyName || "";
    }

    const vendorMatch = vendors.find((vendor) => String(vendor?.id) === String(vendorId));
    return vendorMatch?.name || "";
  }, [vendors]);

  const updateVisibleProducts = useCallback((products, filters, newPage, pageSize) => {
    const matchingProducts = products.filter((product) => (
      matchProductName(product, filters.filterName)
      && matchesMultiFilter(product?.category, filters.filterCategories)
      && matchesMultiFilter(product?.pscCode, filters.filterPscCodes)
      && matchesMultiFilter(getProductVendorName(product), filters.filterVendors)
    ));
    const firstIndex = Math.max(newPage - 1, 0) * pageSize;
    const lastIndex = firstIndex + pageSize;

    setTotalCount(matchingProducts.length);
    setProductsList(matchingProducts.slice(firstIndex, lastIndex));
  }, [getProductVendorName, matchProductName, matchesMultiFilter]);

  const currentFilters = useCallback(() => ({
    filterName: selectedNameFilter,
    filterCategories: selectedCategories,
    filterPscCodes: selectedPscCodes,
    filterVendors: selectedVendors
  }), [selectedNameFilter, selectedCategories, selectedPscCodes, selectedVendors]);

  const fetchAllProducts = useCallback(async () => {
    setProductsLoading(true);

    try {
      const products = sortByMostRecent(normalizeProducts(
        await fetchFilteredProductAll("", "", "", entityId, myListings)
      ));
      setAllProducts(products);
      setFilters(products);
      // The dataset (and the download gate that depends on the viewer) changed; drop any cached ACR
      // availability + export selection so stale rows can't be exported.
      availabilityRequested.current = new Set();
      availabilityAttempts.current = new Map();
      setAcrAvailability({});
      setAcrAddedOn({});
      setSelectedExportIds([]);
    } finally {
      setProductsLoading(false);
    }
  }, [
    entityId,
    myListings,
    fetchFilteredProductAll,
    normalizeProducts,
    setFilters,
    sortByMostRecent
  ]);

  const searchProductList = useCallback((e) => {
    setNameFilter(e.target.value || "");
  }, []);

  const getProductPage = useCallback((newPage, pageSize) => {
    const resolvedPageSize = pageSize || currentPageSize;
    setCurrentPage(newPage);
    updateVisibleProducts(allProducts, currentFilters(), newPage, resolvedPageSize);
  }, [allProducts, currentFilters, currentPageSize, updateVisibleProducts]);

  const changePageHandler = useCallback((pageNumber) => {
    getProductPage(pageNumber, currentPageSize);
  }, [getProductPage, currentPageSize]);

  const clearAllFilters = useCallback(() => {
    setNameFilter("");
    setSelectedCategories([]);
    setSelectedPscCodes([]);
    setSelectedVendors([]);
    setCurrentPage(1);
    updateVisibleProducts(allProducts, {
      filterName: "",
      filterCategories: [],
      filterPscCodes: [],
      filterVendors: []
    }, 1, currentPageSize);
  }, [allProducts, currentPageSize, updateVisibleProducts]);

  useEffect(() => {
    (async () => {
      await fetchAllProducts();
    })();
  }, [user, entityId, myListings, fetchAllProducts]);

  useEffect(() => {
    setCurrentPage(1);
    updateVisibleProducts(allProducts, currentFilters(), 1, currentPageSize);
  }, [
    allProducts,
    selectedNameFilter,
    selectedCategories,
    selectedPscCodes,
    selectedVendors,
    currentPageSize,
    currentFilters,
    updateVisibleProducts
  ]);

  useEffect(() => {
    setFilterTitles(renderDropdownOptions(categories, 'title'));
  }, [categories]);

  useEffect(() => {
    setFilterVendors(renderDropdownOptions(vendors, 'name'));
  }, [vendors]);

  // Resolve which of the currently visible rows have a downloadable in-site ACR. Each product is
  // looked up on its own and cached per product id so paging back doesn't re-request. A failed
  // lookup is retried on this page; only successes (and retries that are exhausted) are cached.
  // Only authenticated users can load reports, so skip entirely for the public table.
  useEffect(() => {
    if (!user) {
      return undefined;
    }
    const toCheck = productsList.filter(
      (product) => product?.id && !availabilityRequested.current.has(String(product.id))
    );
    if (toCheck.length === 0) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      const { availability, addedOn, failedIds } = await getAvailabilityMap(toCheck);
      if (cancelled) {
        return;
      }
      Object.keys(availability).forEach((id) => availabilityRequested.current.add(id));
      setAcrAvailability((prev) => ({ ...prev, ...availability }));
      setAcrAddedOn((prev) => ({ ...prev, ...addedOn }));

      const retryable = failedIds.filter(
        (id) => (availabilityAttempts.current.get(id) || 0) < AVAILABILITY_MAX_RETRIES
      );
      const exhausted = failedIds.filter((id) => !retryable.includes(id));
      exhausted.forEach((id) => availabilityRequested.current.add(id));
      if (exhausted.length > 0) {
        setAcrAvailability((prev) => ({
          ...prev,
          ...Object.fromEntries(exhausted.map((id) => [id, false])),
        }));
      }
      if (retryable.length === 0) {
        return;
      }
      retryable.forEach((id) => {
        availabilityAttempts.current.set(id, (availabilityAttempts.current.get(id) || 0) + 1);
      });
      window.clearTimeout(availabilityRetryTimer.current);
      availabilityRetryTimer.current = window.setTimeout(() => {
        if (!cancelled) {
          setAvailabilityRetryToken((token) => token + 1);
        }
      }, AVAILABILITY_RETRY_MS);
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(availabilityRetryTimer.current);
    };
  }, [productsList, user, getAvailabilityMap, availabilityRetryToken]);

  const categoryOptions = useMemo(() => {
    const isBottom = (title) => BOTTOM_CATEGORY_LABELS.includes(title.trim().toLowerCase());
    const top = filterTitles.filter((title) => !isBottom(title));
    const bottom = filterTitles
      .filter(isBottom)
      .sort((a, b) => (
        BOTTOM_CATEGORY_LABELS.indexOf(a.trim().toLowerCase())
        - BOTTOM_CATEGORY_LABELS.indexOf(b.trim().toLowerCase())
      ));
    return [...top, ...bottom].map((title) => ({ value: title, label: title }));
  }, [filterTitles]);

  const vendorOptions = useMemo(
    () => filterVendors.map((name) => ({ value: name, label: name })),
    [filterVendors]
  );

  const pscOptions = useMemo(
    () => filterPscCodes.map((code) => ({ value: code, label: code })),
    [filterPscCodes]
  );

  const selectableIds = useMemo(
    () => productsList.filter((product) => acrAvailability[product.id] === true).map((product) => product.id),
    [productsList, acrAvailability]
  );

  const selectedIdSet = useMemo(() => new Set(selectedExportIds.map(String)), [selectedExportIds]);

  const selectedProducts = useMemo(
    () => allProducts.filter((product) => selectedIdSet.has(String(product.id))),
    [allProducts, selectedIdSet]
  );

  const toggleSelect = useCallback((productId) => {
    setSelectedExportIds((prev) => (
      prev.some((id) => String(id) === String(productId))
        ? prev.filter((id) => String(id) !== String(productId))
        : [...prev, productId]
    ));
  }, []);

  const toggleSelectAll = useCallback((checked) => {
    setSelectedExportIds((prev) => {
      const pageIds = selectableIds.map(String);
      const withoutPage = prev.filter((id) => !pageIds.includes(String(id)));
      return checked ? [...withoutPage, ...selectableIds] : withoutPage;
    });
  }, [selectableIds]);

  const hasActiveFilters = (
    selectedNameFilter.trim() !== ""
    || selectedCategories.length > 0
    || selectedPscCodes.length > 0
    || selectedVendors.length > 0
  );
  const emptyMessage = hasActiveFilters
    ? "No products match your filters. Try removing or changing some filters."
    : "No products found.";
  // Offer an ACR request from the "no products match" empty state, but only where requesting is
  // possible: the authenticated agency-user listings (which pass onRequestAcr) with filters applied.
  const emptyRequestAcr = onRequestAcr && hasActiveFilters ? onRequestAcr : undefined;

  // One removable chip per applied filter value so users can see and drop individual selections
  // without clearing everything.
  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (selectedNameFilter.trim() !== "") {
      chips.push({
        key: "name",
        label: `Product: ${selectedNameFilter.trim()}`,
        onRemove: () => setNameFilter("")
      });
    }
    selectedCategories.forEach((value) => chips.push({
      key: `category-${value}`,
      label: `Category: ${value}`,
      onRemove: () => setSelectedCategories((prev) => prev.filter((v) => v !== value))
    }));
    selectedVendors.forEach((value) => chips.push({
      key: `vendor-${value}`,
      label: `Vendor: ${value}`,
      onRemove: () => setSelectedVendors((prev) => prev.filter((v) => v !== value))
    }));
    selectedPscCodes.forEach((value) => chips.push({
      key: `psc-${value}`,
      label: `PSC: ${value}`,
      onRemove: () => setSelectedPscCodes((prev) => prev.filter((v) => v !== value))
    }));
    return chips;
  }, [selectedNameFilter, selectedCategories, selectedVendors, selectedPscCodes]);

  const resultsTable = user ? (
    <ProductListTable
      products={productsList || []}
      isLoading={productsLoading}
      emptyMessage={emptyMessage}
      onRequestAcr={emptyRequestAcr}
      acrAvailability={acrAvailability}
      acrAddedOn={acrAddedOn}
      selectableIds={selectableIds}
      selectedIds={selectedExportIds}
      onToggleSelect={toggleSelect}
      onToggleSelectAll={toggleSelectAll}
    />
  ) : (
    <ProductUnauthListTable
      products={productsList || []}
      isLoading={productsLoading}
      emptyMessage={emptyMessage}
    />
  );

  return (
    <section>
      {(isVendorUser || isAgencyUser) && showVendorAction ? (
        <div className="mb-4 flex justify-end">
          <Button
            onClick={() => navigate(PRODUCT_SUBMIT_URL)}
            aria-label="Add new product listing"
          >
            <FontAwesomeIcon icon={faPlus} aria-hidden="true" />&nbsp;
            Add New Product
          </Button>
        </div>
      ) : null}

      <div
        className="product-filter-panel mb-6 rounded-md border border-base bg-base-lightest p-4 text-primary-darker"
        role="search"
        aria-label="Product filters"
      >
        <h2 className="m-0 mb-3 text-base font-bold">Filters</h2>
        <div className="grid grid-cols-12 gap-4 lg:gap-6">
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <label htmlFor="productSearch" className="block">
              <span className="mb-1 h-7 flex items-center font-bold leading-6">Product</span>
              <span className="relative block">
                <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-base-dark">
                  <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" />
                </span>
                <input
                  id="productSearch"
                  type="search"
                  value={selectedNameFilter}
                  onChange={searchProductList}
                  aria-describedby="productSearchCount"
                  className="usa-input m-0 w-full !float-none appearance-none border border-base !pl-9"
                  placeholder="Search products"
                />
              </span>
            </label>
          </div>
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <div className="mb-1 h-7 flex items-center">
              <span id="filterCategory-label" className="font-bold leading-6">Product Category</span>
            </div>
            <SearchableSelect
              id="filterCategory"
              options={categoryOptions}
              value={selectedCategories}
              onChange={setSelectedCategories}
              multiple
              fullWidth
              placeholder="Select categories..."
              filterPlaceholder="Type to filter categories..."
              noResultsText="No categories found"
              clearButtonLabel="Clear selected categories"
              listAriaLabel="Product category options"
            />
          </div>
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <div className="mb-1 h-7 flex items-center">
              <span id="filterVendor-label" className="font-bold leading-6">Product Vendor</span>
            </div>
            <SearchableSelect
              id="filterVendor"
              options={vendorOptions}
              value={selectedVendors}
              onChange={setSelectedVendors}
              multiple
              fullWidth
              placeholder="Select vendors..."
              filterPlaceholder="Type to filter vendors..."
              noResultsText="No vendors found"
              clearButtonLabel="Clear selected vendors"
              listAriaLabel="Product vendor options"
            />
          </div>
          <div className="col-span-12 md:col-span-6 lg:col-span-3">
            <div className="mb-1 h-7 flex items-center gap-1">
              <span id="filterPscCode-label" className="font-bold leading-6">PSC</span>
              <Tooltip label="PSC filter">The Product and Service Codes (PSC) provide codes to describe products, services, and research and
                development (R&D) purchased by the federal government. For more information on the codes and how to use
                them, visit <a href="https://www.acquisition.gov/psc-manual" target="_blank"
                               rel="noreferrer"
                               aria-label="Visit acquisition.gov PSC manual (opens in new window)"
                               className="text-accent-cool">acquisition.gov/psc-manual<span className="usa-sr-only"> (opens in new window)</span><ExternalLinkIcon /></a>.</Tooltip>
            </div>
            <SearchableSelect
              id="filterPscCode"
              options={pscOptions}
              value={selectedPscCodes}
              onChange={setSelectedPscCodes}
              multiple
              fullWidth
              placeholder="Select PSC codes..."
              filterPlaceholder="Type to filter PSC codes..."
              noResultsText="No PSC codes found"
              clearButtonLabel="Clear selected PSC codes"
              listAriaLabel="PSC code options"
            />
          </div>
        </div>
      </div>

      <div className="product-results-toolbar mb-4 flex flex-wrap items-center justify-between gap-3 pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <p id="productSearchCount" className="m-0 text-sm text-base-dark" aria-live="polite">
            Showing {totalCount ?? 0} products
          </p>
          {activeFilterChips.length > 0 ? (
            <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
              {activeFilterChips.map((chip) => (
                <li key={chip.key}>
                  <button
                    type="button"
                    onClick={chip.onRemove}
                    className="inline-flex items-center gap-1 rounded-full border border-base bg-white px-3 py-1 text-sm text-primary-darker"
                    aria-label={`Remove filter ${chip.label}`}
                  >
                    {chip.label}
                    <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {hasActiveFilters ? (
            <button
              type="button"
              className="usa-button usa-button--unstyled"
              onClick={clearAllFilters}
              aria-controls="product_table"
            >
              Clear all filters
            </button>
          ) : null}
        </div>
        {user ? (
          <div className="flex items-center gap-3">
            <span className="usa-sr-only" aria-live="polite">
              {selectedExportIds.length} ACR{selectedExportIds.length === 1 ? "" : "s"} selected for export
            </span>
            <Button
              outline
              onClick={() => setExportWizardOpen(true)}
              disabled={selectedExportIds.length === 0}
              aria-label="Export selected ACRs"
            >
              <FontAwesomeIcon icon={faDownload} aria-hidden="true" />&nbsp;
              Export ACR{selectedExportIds.length > 0 ? ` (${selectedExportIds.length})` : ""}
            </Button>
          </div>
        ) : null}
      </div>

      <div id="product_table" className='overflow-hidden rounded-md border border-base'>
        {resultsTable}
      </div>
      <div className="mt-4">
        <TableNavigation
          pageSize={currentPageSize}
          setPageSize={setCurrentPageSize}
          currentPage={currentPage}
          totalPages={getTotalPages()}
          setPage={changePageHandler}
        />
      </div>
      {user ? (
        <AcrExportWizard
          isOpen={isExportWizardOpen}
          onClose={() => setExportWizardOpen(false)}
          products={selectedProducts}
          buildExportModel={buildExportModel}
        />
      ) : null}
    </section>
  )
}

ProductTableContainer.propTypes = {
  entityId: PropTypes.string,
  showVendorAction: PropTypes.bool,
  myListings: PropTypes.bool,
  onRequestAcr: PropTypes.func,
}
